import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/common/Navbar";
import Board from "../components/game/Board";
import Chips from "../components/game/Chips";
import WheelOverlay from "../components/game/WheelOverlay";
import useRoulette from "../hooks/useRoulette";
import useFitScale from "../hooks/useFitScale";
import useChips from "../hooks/useChips";
import { createConfetti } from "../utils/confetti";
import { fmt } from "../utils/helpers";
import SND from "../utils/sound";
import "../styles/roulette.css";

export default function Game() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const stageRef = useRef(null);
  const fxRef = useRef(null);
  const confettiRef = useRef(null);
  const gameRef = useRef(null);
  const flyId = useRef(0);
  const landTimer = useRef(null);
  const [flies, setFlies] = useState([]);
  const [landKey, setLandKey] = useState(null);
  const [sndOn, setSndOn] = useState(true);

  const { portrait, W, H, scale } = useFitScale(stageRef);
  const celebrate = useCallback(
    (big) => confettiRef.current && confettiRef.current.celebrate(big),
    [],
  );
  const g = useRoulette({ onCelebrate: celebrate });
  const { chips } = useChips();

  // selected chip API ke chips me na ho to pehla chip select karo
  useEffect(() => {
    if (chips.length && !chips.some((c) => c.v === g.chip)) {
      g.setChip(chips[0].v);
    }
  }, [chips]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = createConfetti(fxRef.current, stageRef.current);
    confettiRef.current = c;
    window.addEventListener("resize", c.resize);
    return () => {
      window.removeEventListener("resize", c.resize);
      c.destroy();
      confettiRef.current = null;
    };
  }, []);

  useEffect(() => {
    const wake = () => SND.wake();
    document.addEventListener("pointerdown", wake, { once: true });
    return () => document.removeEventListener("pointerdown", wake);
  }, []);

  useEffect(() => () => clearTimeout(landTimer.current), []);

  // selected chip tray se utha kar clicked number par udta hai, phir bet lagti hai
  const handleBet = (key, el) => {
    const game = gameRef.current;
    const src = game && game.querySelector(".chips .chip.sel");
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (src && el && scale && !reduce && g.phase !== "spin" && g.shown >= g.chip) {
      const o = game.getBoundingClientRect();
      const a = src.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      const w = a.width / scale;
      const stake = 41; // table par rakhe chip ka size (--c * .82)
      flyId.current += 1;
      setFlies((f) => [
        ...f,
        {
          id: flyId.current,
          cls: (chips.find((c) => c.v === g.chip) || {}).c || "c10",
          v: g.chip,
          w,
          x: (a.left - o.left) / scale,
          y: (a.top - o.top) / scale,
          dx: (b.left + b.width / 2 - (a.left + a.width / 2)) / scale,
          dy: (b.top + b.height / 2 - (a.top + a.height / 2)) / scale,
          s: stake / w,
        },
      ]);
      setLandKey(key);
      clearTimeout(landTimer.current);
      landTimer.current = setTimeout(() => setLandKey(null), 250);
    }
    g.placeBet(key);
  };

  const betting = g.phase === "bet";
  const m = g.message;

  return (
    <>
      <Navbar
        menuItems={[
          {
            label: `Signed in: ${user?.username || "Player"}`,
            onClick: () => navigate("/"),
          },
          { label: "My Profile", onClick: () => navigate("/profile") },
          { label: "Bet History", onClick: () => navigate("/my-bets") },
          { label: "Results", onClick: () => navigate("/results") },
          // { label: "Deposit", onClick: () => navigate("/deposit") },
          // { label: "Withdraw", onClick: () => navigate("/withdraw") },
          // { label: "Clear Bets", onClick: g.clearBets },
          {
            label: "Logout",
            onClick: () => {
              logout();
              navigate("/login", { replace: true });
            },
          },
        ]}
      />

      <main className="stage" ref={stageRef}>
        <button
          className="icon-btn home"
          title="Clear bets"
          onClick={g.clearBets}
        >
          &#8962;
        </button>
        <button
          className="icon-btn snd"
          title="Sound"
          onClick={() => setSndOn(SND.toggle())}
        >
          {sndOn ? "\u{1F50A}" : "\u{1F507}"}
        </button>
        <button
          className="icon-btn wrench"
          title={`Fast spin: ${g.fast ? "on" : "off"}`}
          style={{ opacity: g.fast ? 1 : 0.6 }}
          onClick={g.toggleFast}
        >
          {"\u{1F527}"}
        </button>

        <div className="fit" style={{ width: W * scale, height: H * scale }}>
          <div
            ref={gameRef}
            className={`game${portrait ? " portrait" : ""}`}
            style={{ width: W, height: H, transform: `scale(${scale})` }}
          >
            <h1
              className={`message${m.gold ? " gold" : ""}${m.ghost ? " ghost" : ""}${m.pop ? " pop" : ""}`}
            >
              {m.text}
            </h1>

            <button
              className="undo"
              hidden={!(betting && g.stack.length)}
              onClick={g.undo}
            >
              Undo
            </button>

            <button
              className="clear-btn"
              hidden={!(betting && g.totalBet > 0)}
              onClick={g.clearBets}
            >
              Clear
            </button>

            <div className="board-row">
              <Board
                bets={g.bets}
                phase={g.phase}
                win={g.win}
                onBet={handleBet}
                landKey={landKey}
              />
              <div className="history">
                {g.history.map((h) => (
                  <b
                    key={h.id}
                    className={h.color}
                    style={{
                      background:
                        h.color === "red" ? "#c0141c" : h.color === "green" ? "#0a7d2c" : "#111",
                    }}
                  >
                    {h.n}
                  </b>
                ))}
              </div>
            </div>

            <div className="total">
              {g.totalBet ? fmt(g.totalBet) : "\u00a0"}
            </div>

            <Chips chips={chips} selected={g.chip} onSelect={g.setChip} />

            {flies.map((f) => (
              <div
                key={f.id}
                className={`chip flychip ${f.cls}`}
                style={{ left: f.x, top: f.y, "--chip": `${f.w}px`, "--dx": `${f.dx}px`, "--dy": `${f.dy}px`, "--s": f.s }}
                onAnimationEnd={() => setFlies((a) => a.filter((x) => x.id !== f.id))}
              >
                <span>{f.v}</span>
              </div>
            ))}

            <div className="balance">{fmt(g.shown)}</div>

            <button
              className="spin-btn"
              hidden={!(betting && g.totalBet > 0)}
              onClick={g.spin}
            >
              <span className="spin-icon" />
              <b>Spin</b>
            </button>

            <WheelOverlay
              show={g.wheelVisible}
              spin={g.spinJob}
              onLand={g.handleLand}
              onDone={g.handleDone}
            />

            {g.floatWin && (
              <div key={g.floatWin.id} className="floatwin">
                {g.floatWin.text}
              </div>
            )}
          </div>
        </div>

        <canvas id="fx" ref={fxRef} />
      </main>
    </>
  );
}